/**
 * oddit-section-product-spotlight
 * Horizontal product carousel with arrow navigation, optional autoplay, swipe.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-section-product-spotlight';
  var TRACK_SELECTOR = '.oddit-section-product-spotlight__track';
  var CARD_SELECTOR = '.oddit-section-product-spotlight__card';
  var PREV_SELECTOR = '.oddit-section-product-spotlight__arrow--prev';
  var NEXT_SELECTOR = '.oddit-section-product-spotlight__arrow--next';
  var DRAG_THRESHOLD = 10;

  var sectionState = new Map();

  function getCardWidth(track) {
    var card = track.querySelector(CARD_SELECTOR);
    if (!card) return 0;
    var style = getComputedStyle(track);
    var gap = parseInt(style.columnGap, 10) || parseInt(style.gap, 10) || 0;
    return card.offsetWidth + gap;
  }

  function updateArrowStates(section, track) {
    var prevBtn = section.querySelector(PREV_SELECTOR);
    var nextBtn = section.querySelector(NEXT_SELECTOR);
    if (!prevBtn || !nextBtn) return;

    var scrollLeft = Math.round(track.scrollLeft);
    var maxScroll = track.scrollWidth - track.clientWidth;

    prevBtn.disabled = scrollLeft <= 1;
    nextBtn.disabled = scrollLeft >= maxScroll - 1;
  }

  function scrollToDirection(track, direction) {
    var cardWidth = getCardWidth(track);
    if (cardWidth === 0) return;
    track.scrollBy({ left: direction * cardWidth, behavior: 'smooth' });
  }

  function snapToNearest(track) {
    var cardWidth = getCardWidth(track);
    if (cardWidth === 0) return;
    var maxScroll = track.scrollWidth - track.clientWidth;
    var target = Math.round(track.scrollLeft / cardWidth) * cardWidth;
    // The last card's start-aligned snap point sits past maxScroll and can't
    // be reached, so when near the end snap to maxScroll instead of the
    // second-to-last card — that fully reveals the final card.
    if (target > maxScroll || maxScroll - track.scrollLeft < cardWidth / 2) {
      target = maxScroll;
    }
    target = Math.max(0, Math.min(target, maxScroll));
    if (Math.abs(target - track.scrollLeft) < 1) return;
    track.scrollTo({ left: target, behavior: 'smooth' });
  }

  function initSection(section) {
    var track = section.querySelector(TRACK_SELECTOR);
    if (!track) return;

    var prevBtn = section.querySelector(PREV_SELECTOR);
    var nextBtn = section.querySelector(NEXT_SELECTOR);

    var autoplayEnabled = section.getAttribute('data-autoplay') === 'true';
    var autoplaySpeed = parseInt(section.getAttribute('data-autoplay-speed'), 10) || 5;
    var autoplayInterval = null;

    function stopAutoplay() {
      if (autoplayInterval) {
        clearInterval(autoplayInterval);
        autoplayInterval = null;
      }
    }

    function killAutoplay() {
      autoplayEnabled = false;
      stopAutoplay();
    }

    function startAutoplay() {
      if (!autoplayEnabled) return;
      stopAutoplay();
      autoplayInterval = setInterval(function () {
        var maxScroll = track.scrollWidth - track.clientWidth;
        if (Math.round(track.scrollLeft) >= maxScroll - 1) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          scrollToDirection(track, 1);
        }
      }, autoplaySpeed * 1000);
    }

    function onPrev() {
      killAutoplay();
      scrollToDirection(track, -1);
    }

    function onNext() {
      killAutoplay();
      scrollToDirection(track, 1);
    }

    function onScroll() {
      updateArrowStates(section, track);
    }

    function onTouchStart() {
      killAutoplay();
    }

    function onMouseEnter() { stopAutoplay(); }
    function onMouseLeave() { startAutoplay(); }
    function onFocusIn() { stopAutoplay(); }
    function onFocusOut() { startAutoplay(); }

    // Mouse drag-to-scroll (desktop). Touch keeps native scrolling.
    var isDragging = false;
    var dragMoved = false;
    var dragStartX = 0;
    var dragStartScroll = 0;

    function onPointerDown(e) {
      dragMoved = false;
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (track.scrollWidth - track.clientWidth < 1) return;
      isDragging = true;
      dragStartX = e.clientX;
      dragStartScroll = track.scrollLeft;
      killAutoplay();
    }

    function onPointerMove(e) {
      if (!isDragging) return;
      var dx = e.clientX - dragStartX;
      if (!dragMoved) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        dragMoved = true;
        track.classList.add('is-dragging');
        if (track.setPointerCapture) track.setPointerCapture(e.pointerId);
      }
      track.scrollLeft = dragStartScroll - dx;
    }

    function onPointerUp(e) {
      if (!isDragging) return;
      isDragging = false;
      if (!dragMoved) return;
      track.classList.remove('is-dragging');
      if (track.releasePointerCapture && track.hasPointerCapture && track.hasPointerCapture(e.pointerId)) {
        track.releasePointerCapture(e.pointerId);
      }
      snapToNearest(track);
    }

    function onDragClickCapture(e) {
      if (dragMoved) {
        e.preventDefault();
        e.stopPropagation();
        dragMoved = false;
      }
    }

    if (prevBtn) prevBtn.addEventListener('click', onPrev);
    if (nextBtn) nextBtn.addEventListener('click', onNext);
    track.addEventListener('scroll', onScroll, { passive: true });
    track.addEventListener('touchstart', onTouchStart, { passive: true });
    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', onPointerUp);
    track.addEventListener('pointercancel', onPointerUp);
    track.addEventListener('click', onDragClickCapture, true);
    section.addEventListener('mouseenter', onMouseEnter);
    section.addEventListener('mouseleave', onMouseLeave);
    section.addEventListener('focusin', onFocusIn);
    section.addEventListener('focusout', onFocusOut);

    updateArrowStates(section, track);

    var ro = new ResizeObserver(function () {
      updateArrowStates(section, track);
    });
    ro.observe(track);

    startAutoplay();

    sectionState.set(section, {
      prevBtn: prevBtn,
      nextBtn: nextBtn,
      track: track,
      onPrev: onPrev,
      onNext: onNext,
      onScroll: onScroll,
      onTouchStart: onTouchStart,
      onMouseEnter: onMouseEnter,
      onMouseLeave: onMouseLeave,
      onFocusIn: onFocusIn,
      onFocusOut: onFocusOut,
      onPointerDown: onPointerDown,
      onPointerMove: onPointerMove,
      onPointerUp: onPointerUp,
      onDragClickCapture: onDragClickCapture,
      stopAutoplay: stopAutoplay,
      ro: ro
    });
  }

  function destroySection(section) {
    var state = sectionState.get(section);
    if (!state) return;

    if (state.prevBtn) state.prevBtn.removeEventListener('click', state.onPrev);
    if (state.nextBtn) state.nextBtn.removeEventListener('click', state.onNext);
    if (state.track) state.track.removeEventListener('scroll', state.onScroll);
    if (state.track) state.track.removeEventListener('touchstart', state.onTouchStart);
    if (state.track) {
      state.track.removeEventListener('pointerdown', state.onPointerDown);
      state.track.removeEventListener('pointermove', state.onPointerMove);
      state.track.removeEventListener('pointerup', state.onPointerUp);
      state.track.removeEventListener('pointercancel', state.onPointerUp);
      state.track.removeEventListener('click', state.onDragClickCapture, true);
    }
    if (state.ro) state.ro.disconnect();
    if (state.stopAutoplay) state.stopAutoplay();
    section.removeEventListener('mouseenter', state.onMouseEnter);
    section.removeEventListener('mouseleave', state.onMouseLeave);
    section.removeEventListener('focusin', state.onFocusIn);
    section.removeEventListener('focusout', state.onFocusOut);

    sectionState.delete(section);
  }

  document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });
})();
